import * as React from 'react'
import styled, { css } from 'styled-components'
import { HStack, VStack, StackProps } from '@musinsa/mwms-ds'
import FieldLabel, { LABEL_WIDTH_DEFAULT } from '@/components/content/FieldLabel'
import { CSSProperties, useCallback } from 'react'
import { isNil, max } from 'lodash-es'

const FIELD_GAP = 40

export type FieldListType = FieldListItem[][]

export interface FieldListProps {
  className?: string
  labelClassName?: string
  valueClassName?: string
  gap?: StackProps['gap']
  align?: CSSProperties['alignItems']
  gapRow?: StackProps['gap']
  listWidth?: number
  labelWidth?: number
  list: FieldListType
}

interface FieldListItem {
  align?: CSSProperties['alignItems']
  colSpan?: number
  width?: number | string
  className?: string
  label?: React.ReactNode
  value: React.ReactNode
  required?: boolean
  validationValue?: React.ReactNode // 검증 할 때 사용하는 값
}

function FieldList({
  className,
  labelClassName,
  valueClassName,
  align = 'baseline',
  gap = 12,
  gapRow,
  listWidth,
  labelWidth,
  list = [],
}: FieldListProps) {
  const getItemWidth = useCallback(
    (colSpan = 1) => {
      const maxFieldSize = Math.floor((max(list.map((fields) => fields.length)) || 1) / colSpan)
      return `calc(${100 / maxFieldSize}% - ${(FIELD_GAP * (maxFieldSize - 1)) / maxFieldSize}px)`
    },
    [list],
  )

  return (
    <FieldListWrap className={className} gap={gap} $listWidth={listWidth}>
      {list.map((fields, fieldsIdx) => (
        <FieldRow align="baseline" gap={gapRow || FIELD_GAP} key={`field-list-row-${fieldsIdx}`}>
          {fields?.map((field, fieldIdx) => {
            const style: CSSProperties = {}
            if (field.align || align) style.alignItems = field.align || align

            const width = field.width ? (typeof field.width === 'number' ? `${field.width}px` : field.width) : getItemWidth()
            style.minWidth = width
            style.maxWidth = width

            return (
              <FilterItem className={field.className} style={style} key={`field-list-item-${field?.label}-${fieldIdx}`}>
                {!isNil(field?.label) && (
                  <FieldLabel className={labelClassName} required={field.required} width={labelWidth} content={field.label} />
                )}

                <FilterValue className={valueClassName} $label={!!field?.label} $labelWidth={labelWidth}>
                  {field.value}
                </FilterValue>
              </FilterItem>
            )
          })}
        </FieldRow>
      ))}
    </FieldListWrap>
  )
}

const FieldListWrap = styled(VStack)<{ $listWidth?: number }>`
  ${({ $listWidth }) =>
    $listWidth &&
    css`
      max-width: ${$listWidth}px;
    `};
`

const FieldRow = styled(HStack)`
  width: 100%;
`

const FilterItem = styled(HStack)`
  max-width: 100%;
  flex-wrap: nowrap;
  text-align: left;
  align-items: baseline;
`

const FilterValue = styled.div<{ $label: boolean; $labelWidth?: number }>`
  ${({ $label, $labelWidth }) =>
    $label &&
    css`
      width: calc(100% - ${$labelWidth || LABEL_WIDTH_DEFAULT}px - 16px);
    `}
`

export default FieldList
